import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { PERMISSIONS, type Permission, type RoleDto } from "@platform/core";
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Dialog,
  EmptyState,
  Field,
  Input,
  DataGrid,
  useToast,
  type GridColDef,
} from "@platform/ui";
import { useApi, useConsole } from "../context";
import { useT, type MessageKey } from "../i18n";
import { errorText } from "../lib/errors";
import { ConfirmDialog } from "../layout/ConfirmDialog";
import { useGridText } from "../lib/grid";
import { ErrorBanner, PageCard, ReloadButton } from "../layout/Page";

const AREAS: Array<{ id: string; label: MessageKey }> = [
  { id: "users", label: "perm.users" },
  { id: "roles", label: "perm.roles" },
  { id: "groups", label: "perm.groups" },
  { id: "logs", label: "perm.logs" },
  { id: "settings", label: "perm.settings" },
  { id: "clients", label: "perm.clients" },
  { id: "data", label: "perm.data" },
];

export function groupPermissions(
  perms: readonly Permission[],
): Array<{ area: string; permissions: Permission[] }> {
  return AREAS.map((a) => ({
    area: a.id,
    permissions: perms.filter((p) => p.startsWith(`${a.id}:`)),
  })).filter((g) => g.permissions.length);
}

function RoleDialog({ role, onClose }: { role: RoleDto | "new"; onClose: () => void }) {
  const { t } = useT();
  const api = useApi();
  const toast = useToast();
  const qc = useQueryClient();
  const isNew = role === "new";
  const [name, setName] = useState(isNew ? "" : role.name);
  const [description, setDescription] = useState(isNew ? "" : (role.description ?? ""));
  const [perms, setPerms] = useState<Permission[]>(isNew ? [] : role.permissions);
  const save = useMutation({
    mutationFn: () =>
      isNew
        ? api.roles.create({ name, permissions: perms, ...(description ? { description } : {}) })
        : api.roles.update(role.id, { description, permissions: perms }),
    onSuccess: () => {
      toast({ title: t("common.saved"), tone: "success" });
      void qc.invalidateQueries({ queryKey: ["roles"] });
      onClose();
    },
  });
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={isNew ? t("roles.createTitle") : t("roles.editTitle", { name: role.name })}
      closeLabel={t("common.close")}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button loading={save.isPending} onClick={() => save.mutate()} disabled={!name}>
            {t("common.save")}
          </Button>
        </>
      }
    >
      {save.isError && (
        <Alert tone="error" className="mb-4">
          {errorText(save.error)}
        </Alert>
      )}
      <Field label={t("roles.name")} hint={t("roles.nameHint")} required>
        {(p) => (
          <Input
            {...p}
            value={name}
            disabled={!isNew}
            onChange={(e) => setName(e.target.value.toLowerCase())}
            pattern="[a-z][a-z0-9_-]{1,40}"
          />
        )}
      </Field>
      <Field label={t("roles.description")}>
        {(p) => <Input {...p} value={description} onChange={(e) => setDescription(e.target.value)} />}
      </Field>
      <fieldset>
        <legend className="mb-2 text-sm font-medium">{t("roles.permissions")}</legend>
        <div className="grid gap-4 sm:grid-cols-2">
          {groupPermissions(PERMISSIONS).map((g) => (
            <div key={g.area}>
              <p className="mb-1 text-xs font-medium text-ink-2 uppercase">
                {t(AREAS.find((a) => a.id === g.area)!.label)}
              </p>
              {g.permissions.map((p) => (
                <Checkbox
                  key={p}
                  className="py-0.5"
                  label={<code className="text-xs">{p}</code>}
                  checked={perms.includes(p)}
                  onChange={(e) => setPerms(e.target.checked ? [...perms, p] : perms.filter((x) => x !== p))}
                />
              ))}
            </div>
          ))}
        </div>
      </fieldset>
    </Dialog>
  );
}

export function RolesPage() {
  const { t } = useT();
  const api = useApi();
  const { can } = useConsole();
  const qc = useQueryClient();
  const toast = useToast();
  const roles = useQuery({ queryKey: ["roles"], queryFn: api.roles.list });
  const [editing, setEditing] = useState<RoleDto | "new" | null>(null);
  const [deleting, setDeleting] = useState<RoleDto | null>(null);
  const del = useMutation({
    mutationFn: (id: string) => api.roles.remove(id),
    onSuccess: () => {
      setDeleting(null);
      toast({ title: t("common.saved"), tone: "success" });
      void qc.invalidateQueries({ queryKey: ["roles"] });
    },
  });
  const editable = can("roles:write");
  const text = useGridText();
  const columns = useMemo<GridColDef<RoleDto>[]>(
    () => [
      {
        field: "name",
        headerName: t("roles.name"),
        width: 280,
        hideable: false,
        valueFormatter: (_v, r) => `${r.name} ${r.description ?? ""}`.trim(),
        renderCell: ({ row: r }) => (
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium">{r.name}</span>
              {r.builtIn && <Badge>{t("roles.builtIn")}</Badge>}
            </div>
            {r.description && <p className="truncate text-xs text-ink-2">{r.description}</p>}
          </div>
        ),
      },
      {
        field: "permissions",
        headerName: t("roles.permissions"),
        type: "singleSelect",
        minWidth: 300,
        sortable: false,
        filterOperators: ["is", "not", "isAnyOf"],
        valueOptions: PERMISSIONS.map((p) => ({ value: p, label: p })),
        renderCell: ({ row: r }) => (
          <div className="flex flex-wrap gap-1 py-0.5">
            {r.permissions.length === 0 ? (
              <span className="text-ink-3">{t("common.none")}</span>
            ) : (
              r.permissions.map((p) => (
                <Badge key={p} tone="info">
                  {p}
                </Badge>
              ))
            )}
          </div>
        ),
      },
      {
        field: "builtIn",
        headerName: t("roles.builtIn"),
        type: "boolean",
        width: 110,
        hiddenByDefault: true,
        valueFormatter: (v) => (v ? t("roles.builtIn") : ""),
      },
      {
        field: "actions",
        headerName: "",
        type: "actions",
        width: 170,
        hideable: false,
        resizable: false,
        disableExport: true,
        align: "right",
        renderCell: ({ row: r }) =>
          editable && !r.builtIn ? (
            <>
              <Button variant="text" size="sm" onClick={() => setEditing(r)}>
                {t("common.edit")}
              </Button>
              <Button variant="text" size="sm" className="text-danger" onClick={() => setDeleting(r)}>
                {t("common.delete")}
              </Button>
            </>
          ) : r.builtIn ? (
            <span className="text-xs text-ink-3">{t("common.readOnly")}</span>
          ) : null,
      },
    ],
    [t, editable],
  );
  return (
    <PageCard>
      {roles.isError && <ErrorBanner error={roles.error} onRetry={() => void roles.refetch()} />}
      <DataGrid
        id="roles"
        label={t("nav.roles")}
        localeText={text}
        rows={roles.data?.items ?? []}
        columns={columns}
        loading={roles.isFetching}
        pagination={false}
        getRowAttributes={(r) => ({ "data-testid": "role-row", "data-role": r.id })}
        noRowsContent={<EmptyState title={t("roles.empty")} />}
        toolbarActions={
          <>
            <ReloadButton onClick={() => void roles.refetch()} />
            {editable && (
              <Button onClick={() => setEditing("new")}>
                <Plus className="size-4" aria-hidden />
                {t("roles.add")}
              </Button>
            )}
          </>
        }
      />
      {editing && <RoleDialog role={editing} onClose={() => setEditing(null)} />}
      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(o) => !o && setDeleting(null)}
        title={t("common.delete")}
        confirmLabel={t("common.delete")}
        destructive
        loading={del.isPending}
        onConfirm={() => deleting && del.mutate(deleting.id)}
      >
        <p className="text-sm">{t("roles.deleteConfirm", { name: deleting?.name ?? "" })}</p>
        {del.isError && (
          <Alert tone="error" className="mt-3">
            {errorText(del.error)}
          </Alert>
        )}
      </ConfirmDialog>
    </PageCard>
  );
}
